<template>
  <v-dialog v-model="dialog" eager max-width="90%" scrollable>
    <v-card>
      <!-- Enhanced Header -->
      <v-card-title class="py-4">
        <v-row align="center" no-gutters>
          <v-col cols="12" md="7" class="d-flex align-center">
            <v-icon left>mdi-chart-line-variant</v-icon>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <span
                  v-bind="attrs"
                  v-on="on"
                  class="text-h6 font-weight-medium text-truncate"
                  style="max-width: 500px"
                >
                  {{ selectedProduct.name }}
                </span>
              </template>
              <span>{{ selectedProduct.name }}</span>
            </v-tooltip>
          </v-col>
          <v-col cols="12" md="5" class="d-flex align-center justify-end">
            <v-menu
              ref="menu"
              v-model="menu"
              :close-on-content-click="false"
              :return-value.sync="dates"
              transition="scale-transition"
              offset-y
              min-width="290px"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="dateRangeText"
                  placeholder="Select date range"
                  prepend-inner-icon="mdi-calendar-range"
                  readonly
                  dense
                  hide-details
                  outlined
                  class="date-input"
                  v-bind="attrs"
                  v-on="on"
                ></v-text-field>
              </template>

              <v-date-picker
                v-show="!months_btn"
                v-model="dates"
                :max="max_date"
                no-title
                range
                scrollable
              >
                <v-spacer></v-spacer>
                <v-btn text color="primary" @click="menu = false"> Cancel </v-btn>
                <v-btn text color="primary" @click="changeRange"> OK </v-btn>
              </v-date-picker>

              <v-date-picker
                v-show="months_btn"
                v-model="dates"
                :max="max_date"
                no-title
                range
                scrollable
                type="month"
              >
                <v-spacer></v-spacer>
                <v-btn text color="primary" @click="menu = false"> Cancel </v-btn>
                <v-btn text color="primary" @click="changeRange"> OK </v-btn>
              </v-date-picker>
            </v-menu>

            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn v-bind="attrs" v-on="on" icon @click="reset" class="ml-2">
                  <v-icon>mdi-refresh</v-icon>
                </v-btn>
              </template>
              <span>Reset to default range</span>
            </v-tooltip>

            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn v-bind="attrs" v-on="on" icon @click="dialog = false" class="ml-2">
                  <v-icon>mdi-close</v-icon>
                </v-btn>
              </template>
              <span>Close</span>
            </v-tooltip>
          </v-col>
        </v-row>
      </v-card-title>

      <v-card-text class="pa-4">
        <v-container fluid class="pa-0">
          <v-row>
            <!-- Chart Section -->
            <v-col cols="12">
              <v-card flat outlined class="chart-container">
                <v-row class="ma-0">
                  <!-- Product Image -->
                  <v-col cols="12" md="3" class="product-image-section" :key="loadKey">
                    <div class="image-wrapper">
                      <img
                        :src="this.selectedProduct.image"
                        @error="getCompetitorImage($event, selected_product)"
                        class="product-image-display"
                      />
                    </div>
                  </v-col>

                  <!-- Chart Area -->
                  <v-col cols="12" md="9" class="chart-section">
                    <loadingProgress :loading="loading" text="Loading chart data..." />
                    <div v-show="!loading" class="chart-content">
                      <!-- Chart Controls -->
                      <v-row class="mb-3">
                        <v-col
                          cols="12"
                          class="d-flex align-center justify-space-between flex-wrap"
                        >
                          <!-- Time Interval Toggle -->
                          <div class="d-flex align-center flex-wrap" style="gap: 8px">
                            <v-btn-toggle
                              v-model="btns_group"
                              color="primary"
                              mandatory
                              class="mb-2 mb-md-0"
                              small
                            >
                              <v-btn
                                value="Days"
                                @click="changeInterval('day')"
                                class="text-none"
                                small
                              >
                                <v-icon left small>mdi-calendar-today</v-icon>
                                Days
                              </v-btn>
                              <v-btn
                                value="Months"
                                @click="changeInterval('month')"
                                class="text-none"
                                small
                              >
                                <v-icon left small>mdi-calendar-month</v-icon>
                                Months
                              </v-btn>
                            </v-btn-toggle>

                            <!-- Google Shopping Toggle -->
                            <v-tooltip bottom v-if="googleShoppingKeys.length > 0">
                              <template v-slot:activator="{ on, attrs }">
                                <v-btn
                                  v-bind="attrs"
                                  v-on="on"
                                  small
                                  outlined
                                  :color="hideGoogleShopping ? 'grey' : 'primary'"
                                  @click="hideGoogleShopping = !hideGoogleShopping"
                                  class="text-none mb-2 mb-md-0"
                                >
                                  <img
                                    :src="require('../../assets/google_shopping_favicon.png')"
                                    width="14"
                                    height="14"
                                    class="mr-1"
                                    :style="hideGoogleShopping ? 'opacity:0.4;filter:grayscale(1)' : ''"
                                  />
                                  {{ hideGoogleShopping ? 'Show Google Shopping offers' : 'Hide Google Shopping offers' }}
                                </v-btn>
                              </template>
                              <span>{{ hideGoogleShopping ? 'Show Google Shopping' : 'Hide Google Shopping' }}</span>
                            </v-tooltip>
                          </div>

                          <!-- Export Actions -->
                          <div class="d-flex align-center">
                            <v-tooltip bottom>
                              <template v-slot:activator="{ on, attrs }">
                                <v-btn
                                  v-bind="attrs"
                                  v-on="on"
                                  outlined
                                  small
                                  color="primary"
                                  @click="saveAsImage"
                                  class="mr-2 text-none"
                                >
                                  <v-icon left small>mdi-image-outline</v-icon>
                                  Save as Image
                                </v-btn>
                              </template>
                              <span>Download chart as JPG image</span>
                            </v-tooltip>

                            <v-menu bottom offset-y>
                              <template v-slot:activator="{ on: menu }">
                                <v-btn v-on="{ ...menu }" small color="primary" class="text-none">
                                  <v-icon left small>mdi-download</v-icon>
                                  Export Data
                                </v-btn>
                              </template>
                              <v-list>
                                <v-list-item @click="exportChart(false)">
                                  <v-list-item-icon>
                                    <v-icon small>mdi-file-chart-outline</v-icon>
                                  </v-list-item-icon>
                                  <v-list-item-title>Export Current Range</v-list-item-title>
                                </v-list-item>
                                <v-list-item @click="exportChart(true)">
                                  <v-list-item-icon>
                                    <v-icon small>mdi-file-table-outline</v-icon>
                                  </v-list-item-icon>
                                  <v-list-item-title>Export Full Data</v-list-item-title>
                                </v-list-item>
                              </v-list>
                            </v-menu>
                          </div>
                        </v-col>
                      </v-row>

                      <!-- Chart Canvas -->
                      <v-row>
                        <v-col cols="12" class="chart-canvas-wrapper">
                          <div class="chart-canvas-container">
                            <canvas id="historiqueChart"></canvas>
                          </div>
                        </v-col>
                      </v-row>

                      <!-- Range Slider -->
                      <v-row class="mt-2">
                        <v-col cols="12">
                          <v-range-slider
                            v-model="range"
                            :max="max"
                            :min="min"
                            hide-details
                            thumb-label="always"
                            color="primary"
                            track-color="grey lighten-2"
                            @input="rangeChanged()"
                            class="range-slider"
                          >
                            <template v-slot:prepend>
                              <v-chip small outlined color="primary" class="date-chip">
                                {{ labels[range[0]] }}
                              </v-chip>
                            </template>
                            <template v-slot:append>
                              <v-chip small outlined color="primary" class="date-chip">
                                {{ labels[range[1]] }}
                              </v-chip>
                            </template>
                          </v-range-slider>
                        </v-col>
                      </v-row>
                    </div>
                  </v-col>
                </v-row>
              </v-card>
            </v-col>
            <!-- Product Details Section -->
            <v-col cols="12" class="mt-4">
              <v-card flat outlined>
                <v-card-title class="grey lighten-4 py-3">
                  <v-icon left color="primary">mdi-information-outline</v-icon>
                  <span class="text-subtitle-1 font-weight-medium">Product Details</span>

                  <v-spacer></v-spacer>

                  <v-btn-toggle
                    v-if="website == 'sfr.fr'"
                    v-model="tables_btns_group"
                    color="primary"
                    mandatory
                    dense
                  >
                    <v-btn small value="Prices" @click="show_prices = true">
                      <v-icon left small>mdi-currency-usd</v-icon>
                      Prices
                    </v-btn>
                    <v-btn small value="Engagements" @click="show_prices = false">
                      <v-icon left small>mdi-handshake-outline</v-icon>
                      Engagements
                    </v-btn>
                  </v-btn-toggle>
                </v-card-title>

                <v-card-text class="pa-4">
                  <v-row>
                    <!-- Product Info -->
                    <v-col cols="12" md="3">
                      <v-card flat outlined class="info-card">
                        <v-simple-table class="product-info-table">
                          <template v-slot:default>
                            <tbody v-html="infoSectionHtml"></tbody>
                          </template>
                        </v-simple-table>
                      </v-card>
                    </v-col>

                    <!-- Price Statistics -->
                    <v-col cols="12" md="9">
                      <productsLastStatCustom
                        v-if="user_website.id == source_id"
                        v-show="show_prices"
                        :product_match="this.selected_product"
                        :hide_google_shopping="hideGoogleShopping"
                      />
                      <productsLastStat
                        v-else
                        v-show="show_prices"
                        :product_match="this.selected_product"
                        :hide_google_shopping="hideGoogleShopping"
                        @show-validate-modal="forwardValidateModal"
                      />
                      <div v-show="!show_prices">
                        <offerPlansTable :competitors="offer_engagements" />
                      </div>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </v-container>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>
<script>
  import Chart from 'chart.js'
  import productsLastStat from './productsLastStat'
  import productsLastStatCustom from './productsLastStatCustom'
  import offerPlansTable from './offerPlansTable'
  import moment from 'moment'
  import axios from 'axios'
  import XLSX from 'xlsx/xlsx.mini'

  const isEmptyValue = (v) => v === null || v === undefined || v === ''

  // A price of 0 means "no price available": it breaks the line and noPricePlugin draws a dashed
  // segment over it. Regular gaps are forward-filled with invisible points so they stay solid,
  // because spanGaps would also draw a solid line over the zero runs.
  function buildSeries(raw) {
    const noPrice = raw.map((v) => !isEmptyValue(v) && Number(v) === 0)
    const lastIndex = raw.reduce((acc, v, i) => (isEmptyValue(v) ? acc : i), -1)
    const filled = []
    let last = null
    const data = raw.map((v, i) => {
      filled[i] = false
      if (noPrice[i]) return (last = null)
      if (!isEmptyValue(v)) return (last = v)
      if (last === null || i > lastIndex) return null
      filled[i] = true
      return last
    })
    return {
      data,
      rawData: raw,
      noPrice,
      pointRadius: filled.map((f) => (f ? 0 : 3)),
      pointHoverRadius: filled.map((f) => (f ? 0 : 4)),
      pointHitRadius: filled.map((f) => (f ? 0 : 1)),
    }
  }

  function findPoint(data, from, step) {
    for (let k = from; k >= 0 && k < data.length; k += step) {
      if (data[k] !== null) return k
    }
    return -1
  }

  const noPricePlugin = {
    afterDatasetsDraw(chart) {
      const ctx = chart.ctx
      const area = chart.chartArea
      ctx.save()
      ctx.beginPath()
      ctx.rect(area.left, area.top, area.right - area.left, area.bottom - area.top)
      ctx.clip()
      ctx.setLineDash([3, 3])
      chart.data.datasets.forEach((dataset, di) => {
        if (!dataset.noPrice || !chart.isDatasetVisible(di)) return
        const points = chart.getDatasetMeta(di).data
        const view = (k) => points[k]._view
        let i = 0
        while (i < dataset.noPrice.length) {
          if (!dataset.noPrice[i]) {
            i++
            continue
          }
          const start = i
          while (i < dataset.noPrice.length && dataset.noPrice[i]) i++
          const prev = findPoint(dataset.data, start - 1, -1)
          const next = findPoint(dataset.data, i, 1)
          if (prev < 0 && next < 0) continue

          // Stay at the last known price, then step to the next one like the stepped line does
          const y = prev >= 0 ? view(prev).y : view(next).y
          const x0 = prev >= 0 ? view(prev).x : view(start).x
          const x1 = next >= 0 ? view(next).x : view(i - 1).x
          ctx.beginPath()
          ctx.strokeStyle = dataset.borderColor || '#9e9e9e'
          ctx.lineWidth = dataset.borderWidth || 1
          ctx.moveTo(x0, y)
          ctx.lineTo(x1, y)
          if (next >= 0) ctx.lineTo(x1, view(next).y)
          ctx.stroke()
        }
      })
      ctx.restore()
    },
  }

  export default {
    components: {
      productsLastStatCustom,
      productsLastStat,
      offerPlansTable,
    },
    props: ['historgram_data', 'selected_product', 'show', 'component_name'],
    mounted() {
      this.initHistogramChart()
    },
    data() {
      return {
        loadKey: 0,
        account_id: localStorage.getItem('account_id'),
        website: localStorage.getItem('website_alias'),
        user_website: localStorage.user_website ? JSON.parse(localStorage.user_website) : {},
        source_id: '6019aee744817b0f85ff85bb',
        min: 0,
        max: 100,
        range: [0, 100],
        labels: [],
        show_prices: true,
        chartData: {
          data: {
            labels: [],
            datasets: [],
          },
        },
        selectedProduct: {
          name: '',
          url: '',
          ean: '',
          brand: '',
          price: '',
          high: '',
          low: '',
          availability: true,
        },
        dates: [],
        months_dates: [],
        menu: false,
        no_data: false,
        interval: 'day',
        loading: true,
        infoSectionHtml: '',
        historiqueChart: '',
        dialog: false,
        btns_group: 'Days',
        tables_btns_group: 'Prices',
        from: '0',
        to: '0',
        offer_engagements: [],
        months_btn: false,
        max_date: '',
        hideGoogleShopping: false,
        googleShoppingKeys: [],
      }
    },
    computed: {
      dateRangeText() {
        return this.dates.join(' ~ ')
      },
    },
    methods: {
      getCompetitorImage(e, match) {
        let image = ''
        const website = Object.keys(match).find(
          (key) => match[key].product && match[key].product.image && match[key].product.validated,
        )
        if (website) image = match[website].product.image
        e.target.src = image
      },
      async changeRange() {
        this.$refs.menu.save(this.dates)
        let temp1, temp2

        if (this.dates.length) {
          temp1 = this.dates[0].split('-')
          let x = new Date(
            Date.UTC(temp1[0], temp1[1] - 1, temp1[2] != undefined ? temp1[2] : 1, 0, 0, 0),
          )
          temp2 = this.dates[1] == undefined ? this.dates[0].split('-') : this.dates[1].split('-')
          let y = new Date(
            Date.UTC(temp2[0], temp2[1] - 1, temp2[2] != undefined ? temp2[2] : 1, 22, 0, 0),
          )
          if (+x <= +y) {
            this.from = x.getTime()
            this.to = y.getTime()
          } else {
            y.setHours(0)
            x.setHours(22)
            this.from = y.getTime()
            this.to = x.getTime()
          }
        } else {
          this.to = 0
          this.from = 0
        }

        this.loading = true
        this.no_data = false
        await this.getOfferHistorique()
        this.loading = false
      },
      reset() {
        this.dates = []
        this.changeRange()
      },
      rangeChanged() {
        let min_index = this.chartData.data.labels.indexOf(this.labels[this.range[0]])
        let max_index = this.chartData.data.labels.indexOf(this.labels[this.range[1]]) + 1

        this.historiqueChart.data.labels = this.chartData.data.labels.slice(min_index, max_index)
        let datasets = []
        this.chartData.data.datasets.forEach((dataset) => {
          if (this.hideGoogleShopping && this.googleShoppingKeys.includes(dataset.label)) return
          let temp = { ...dataset }
          delete temp._meta
          ;['data', 'rawData', 'noPrice', 'pointRadius', 'pointHoverRadius', 'pointHitRadius'].forEach(
            (prop) => {
              if (Array.isArray(temp[prop])) temp[prop] = temp[prop].slice(min_index, max_index)
            },
          )

          datasets.push(temp)
        })
        this.historiqueChart.data.datasets = datasets
        this.historiqueChart.update()
      },
      exportChart(fullData) {
        const productName = this.selected_product.product_name || this.selectedProduct.name || ''
        const clientRef = this.selected_product.match_user_ref || ''
        const brand = this.selected_product.product_brand || this.selectedProduct.brand || ''

        const source = fullData ? this.chartData : this.historiqueChart

        // Build ordered list of all website aliases from localStorage
        const allWebsites = JSON.parse(localStorage.user_websites || '[]').map((w) => w.alias)

        // Also include any keys present in the source that aren't in allWebsites (e.g. _1/_2 duplicates)
        const sourceKeys = source.data.datasets.map((d) => d.label)
        const extraKeys = sourceKeys.filter((k) => !allWebsites.includes(k))
        const orderedWebsites = [...allWebsites, ...extraKeys]

        // Map dataset label → data array for quick lookup
        const datasetMap = {}
        source.data.datasets.forEach((dataset) => {
          datasetMap[dataset.label] = dataset.rawData || dataset.data
        })

        let data_to_export = []
        source.data.labels.forEach((elt, index) => {
          let row = { date: elt, sku: clientRef, name: productName, brand: brand }
          orderedWebsites.forEach((alias) => {
            const val = datasetMap[alias] ? datasetMap[alias][index] : null
            row[alias] = val ? val : ''
          })
          data_to_export.push(row)
        })

        let workSheet = XLSX.utils.json_to_sheet(data_to_export)
        let wb = XLSX.utils.book_new()
        XLSX.utils.book_append_sheet(wb, workSheet, 'chart data')
        XLSX.writeFile(wb, `chart_export_${productName}.xlsx`)
      },
      async initialisation() {
        this.dates = []
        this.loading = true
        this.initSelectedProduct()
        this.getInfoSections()
        await this.getOfferHistorique()
        this.loading = false
        this.getEngagements()
      },
      changeInterval(value) {
        this.dates = []
        this.interval = value
        this.months_btn = value == 'month'
      },
      async getEngagements() {
        await axios
          .get('/api/products/getengagements', {
            params: {
              account: this.account_id,
              id_match: this.selected_product.match_id,
            },
          })
          .then((res) => {
            this.offer_engagements = res.data
          })
      },
      async setUserWebsites() {
        await axios
          .get('/api/websites/list', {
            params: {
              account: this.account_id,
            },
          })
          .then((res) => {
            localStorage.setItem(`user_website`, JSON.stringify(res.data.account.website))
            localStorage.setItem(`user_websites`, JSON.stringify(res.data.account.websites))
          })
      },
      async getOffers(out_dated) {
        return await axios
          .get(`/api/products/history`, {
            params: {
              interval: this.interval,
              account: this.account_id,
              user: localStorage.getItem('user_id'),
              match: this.selected_product.match_id,
              component: this.component_name,
              from: this.from,
              to: this.to,
            },
          })
          .then((res) => {
            Object.keys(out_dated).map((key) => {
              if (res.data.websiteData[key]) {
                let from = res.data.labels.indexOf(out_dated[key]) + 1
                let to = res.data.websiteData[key].length - from
                res.data.websiteData[key].splice(from, to)
              }
            })
            return res.data
          })
      },
      validateLastChangedDate() {
        let out_dated_product = {}
        Object.keys(this.selected_product).forEach((key) => {
          if (this.selected_product[key] && this.selected_product[key].product) {
            let update_date = this.selected_product[key].product.offers[0].updateDate.split(' ')[0]
            let interval = this.interval == 'day' ? 'YYYY-MM-DD' : 'YYYY-MM'
            if (update_date.indexOf(moment().format(interval)) == -1)
              out_dated_product[key] = update_date
          }
        })
        return out_dated_product
      },
      // this function used to get offers changes over time
      async getOfferHistorique() {
        let out_dated = this.validateLastChangedDate()
        //fetch if the are any new websites for this account
        this.setUserWebsites()
        this.chartData.data.datasets = []
        this.chartData.data.labels = []
        const dataResponse = await this.getOffers(out_dated)

        //insert datasets for chartjs
        this.chartData.data.labels = dataResponse.labels

        // Rebuild Google Shopping keys from the current selected_product
        this.googleShoppingKeys = Object.keys(this.selected_product).filter((key) => {
          return (
            this.selected_product[key] &&
            this.selected_product[key].product &&
            this.selected_product[key].product.isGoogleShoppingProduct
          )
        })

        Object.keys(dataResponse.websiteData).forEach((key) => {
          const websiteColorTest = JSON.parse(localStorage.user_websites).filter((w) => {
            return w.alias == key
          })
          let websiteColor = JSON.parse(localStorage.user_websites).filter(
            (w) => w.alias == key.replace(/_\d*$/, ''),
          )
          websiteColor = websiteColor.length ? websiteColor[0].config.color : ''
          const dataset = {
            label: key,
            ...buildSeries(dataResponse.websiteData[key]),
            spanGaps: false, // gaps are forward-filled by buildSeries, zeros are drawn dashed
            fill: false,
            steppedLine: true,
            borderColor: websiteColor,
            backgroundColor: websiteColor,
            borderWidth: 1,
          }
          this.chartData.data.datasets.push(dataset)
        })

        this.historiqueChart.data.labels = this.chartData.data.labels

        this.labels = this.chartData.data.labels.map((elt) => elt.split(' ')[0])
        this.max = this.labels.length - 1
        this.range = [0, this.labels.length]
        this.historiqueChart.data.datasets = this.hideGoogleShopping
          ? this.chartData.data.datasets.filter(
              (d) => !this.googleShoppingKeys.includes(d.label),
            )
          : this.chartData.data.datasets
        this.historiqueChart.update()
        this.max_date = moment(Date.now()).format('YYYY-MM-DD')      },
      // this function to init Histroque chart
      initHistogramChart() {
        let ctx = 'historiqueChart'
        this.historiqueChart = new Chart(ctx, {
          type: 'line',
          data: this.chartData,
          plugins: [noPricePlugin],
          options: {
            spanGaps: true,
            responsive: true,
            maintainAspectRatio: false,
            animation: {
              duration: 0,
            },
            hover: {
              mode: 'index',
              intersect: false,
            },
            tooltips: {
              // Show every website's price for the hovered date, cheapest first
              mode: 'index',
              intersect: false,
              itemSort: (a, b) => a.yLabel - b.yLabel,
              callbacks: {
                label: (item, data) =>
                  `${data.datasets[item.datasetIndex].label}: ${this.singlePriceHandler(item.yLabel)}`,
                afterBody: (items, data) => {
                  if (!items.length) return []
                  const index = items[0].index
                  return data.datasets
                    .filter(
                      (dataset, di) =>
                        dataset.noPrice &&
                        dataset.noPrice[index] &&
                        this.historiqueChart.isDatasetVisible(di),
                    )
                    .map((dataset) => `${dataset.label}: No price available`)
                },
              },
            },
            scales: {
              yAxes: [
                {
                  gridLines: {
                    display: false,
                  },
                  ticks: {
                    beginAtZero: false,
                    callback: (value) => this.singlePriceHandler(value) || value,
                  },
                },
              ],
            },
          },
        })
      },
      buildImageSrc(item) {
        if (item && item.match(/data:image\/.*/g)) return item.match(/data:image\/.*/g)[0]
        return item
      },
      initSelectedProduct() {
        this.loadKey++
        this.selectedProduct.name = this.selected_product.product_name
        this.selectedProduct.url = this.selected_product.product_url
        this.selectedProduct.image = this.buildImageSrc(this.selected_product.product_image)
        this.selectedProduct.high = this.selected_product.hight_price
        this.selectedProduct.low = this.selected_product.low_price
        this.selectedProduct.price = this.selected_product.product_offer.price
        this.selectedProduct.brand = this.selected_product.product_brand
        this.selectedProduct.availability =
          this.selected_product.product_offer.availability || 'NotFound'
      },
      getInfoSections() {
        let infoSection = ``
        let price = ``
        let hight = ``
        let low = ``

        Object.keys(this.selectedProduct).map((key) => {
          switch (key) {
            case 'brand':
              infoSection = infoSection.concat(`
                        <tr class="simple-table-row-height">
                            <td class="text-body-2">Brand</td>
                            <td class="text--secondary font-weight-medium text-body-2">${this.selectedProduct[
                              key
                            ].toUpperCase()}</td>
                        </tr>`)
              break
            case 'price':
              //formating price later, each region with a deffirent formatter
              //text colors changed when the price is higher or lower
              price = this.selectedProduct[key]
              infoSection = infoSection.concat(`
                        <tr class="simple-table-row-height">
                            <td class="text-body-2">My Price</td>
                            <td class="text--secondary font-weight-bold text-body-1">${
                              price
                                ? this.singlePriceHandler(price)
                                : '<span class="grey--text">Not found</span>'
                            }</td>
                        </tr>`)
              break
            case 'high':
              hight = this.selectedProduct.high
              infoSection = infoSection.concat(`
                        <tr class="simple-table-row-height">
                            <td class="text-body-2">Highest Price</td>
                            <td class="error--text font-weight-bold text-body-1">${
                              hight
                                ? this.singlePriceHandler(hight)
                                : '<span class="grey--text">Not found</span>'
                            }</td>
                        </tr>`)
              break
            case 'low':
              low = this.selectedProduct.low
              infoSection = infoSection.concat(`
                        <tr class="simple-table-row-height">
                            <td class="text-body-2">Lowest Price</td>
                            <td class="success--text font-weight-bold text-body-1">${
                              low
                                ? this.singlePriceHandler(low)
                                : '<span class="grey--text">Not found</span>'
                            }</td>
                        </tr>`)
              break
            case 'availability': {
              const avail = this.selectedProduct.availability
              let stockLabel, stockColor, stockIcon
              if (avail === 'InStock') {
                stockLabel = 'In Stock'
                stockColor = '#4caf50'
                stockIcon = 'mdi-check-circle-outline'
              } else if (avail === 'OutOfStock') {
                stockLabel = 'Out of Stock'
                stockColor = '#ef5350'
                stockIcon = 'mdi-close-circle-outline'
              } else {
                stockLabel = 'Not Found'
                stockColor = '#9e9e9e'
                stockIcon = 'mdi-help-circle-outline'
              }
              const bgColor =
                avail === 'InStock' ? '#e8f5e9' : avail === 'OutOfStock' ? '#fce4ec' : '#f5f5f5'
              const textColor =
                avail === 'InStock' ? '#2e7d32' : avail === 'OutOfStock' ? '#c62828' : '#757575'
              infoSection = infoSection.concat(`
                        <tr class="simple-table-row-height">
                            <td class="text-body-2">Stock Status</td>
                            <td>
                              <span style="display:inline-flex;align-items:center;padding:4px 10px;border-radius:20px;background-color:${bgColor};">
                                <i class="v-icon mdi ${stockIcon}" style="font-size:14px;color:${stockColor};margin-right:4px;"></i>
                                <span style="font-size:0.75rem;font-weight:500;color:${textColor};">${stockLabel}</span>
                              </span>
                            </td>
                        </tr>`)
              break
            }
          }
        })
        this.infoSectionHtml = infoSection
      },
      forwardValidateModal() {
        this.$emit('show-validate-modal', {
          item: this.selected_product,
          data: [this.selected_product],
          pagination: { from: 0, size: 1 },
          searchParams: {},
        })
      },
      saveAsImage() {
        var url_base64jp = document.getElementById('historiqueChart').toDataURL('image/jpg')

        let link = document.createElement('a')
        link.href = url_base64jp
        link.setAttribute('download', this.selectedProduct.name + '_chart.jpg')
        document.body.appendChild(link)
        link.click()
        link.remove()
      },
    },
    watch: {
      hideGoogleShopping() {
        this.rangeChanged()
      },
      interval: async function (newValue, oldValue) {
        this.loading = true
        this.to = 0
        this.from = 0
        this.dates = []
        await this.getOfferHistorique()
        this.loading = false
      },
      selected_product: {
        handler() {},
      },
      show: {
        handler() {
          this.to = 0
          this.from = 0
          this.dates = []
          this.hideGoogleShopping = false
          this.initialisation()
          this.dialog = true
        },
      },
    },
  }
</script>
<style scoped>
  /* Enhanced Modal Styling */
  .date-input {
    max-width: 300px;
  }

  /* Chart Container */
  .chart-container {
    border-radius: 8px;
    overflow: hidden;
  }

  /* Product Image Section */
  .product-image-section {
    background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
    border-right: 1px solid #e0e0e0;
  }

  .image-wrapper {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: white;
    border-radius: 8px;
    padding: 20px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }

  .product-image-display {
    max-width: 100%;
    max-height: 400px;
    object-fit: contain;
    object-position: center;
  }

  /* Chart Section */
  .chart-section {
    padding: 20px;
  }

  .chart-content {
    width: 100%;
  }

  .chart-canvas-wrapper {
    background: #fafafa;
    border-radius: 4px;
    padding: 16px;
  }

  .chart-canvas-container {
    position: relative;
    width: 100%;
    height: 320px;
  }

  @media (max-width: 960px) {
    .chart-canvas-container {
      height: 260px;
    }
  }

  @media (max-width: 600px) {
    .chart-canvas-container {
      height: 220px;
    }
  }

  /* Range Slider */
  .range-slider {
    padding: 0 16px;
  }

  .date-chip {
    min-width: 100px;
    justify-content: center;
  }

  /* Product Info Card */
  .info-card {
    border-radius: 8px;
    height: 100%;
  }

  .product-info-table {
    height: 100%;
  }

  .product-info-table tbody tr {
    height: 48px;
  }

  .product-info-table tbody tr td:first-child {
    font-weight: 500;
    color: rgba(0, 0, 0, 0.6);
    width: 40%;
  }

  .product-info-table tbody tr td:last-child {
    font-weight: 600;
  }

  /* Responsive adjustments */
  @media (max-width: 960px) {
    .product-image-section {
      border-right: none;
      border-bottom: 1px solid #e0e0e0;
      min-height: 250px;
    }

    .date-input {
      max-width: 100%;
    }
  }

  /* Simple table row height */
  .simple-table-row-height {
    height: 48px;
  }
</style>
